import { z } from "zod";
import { type graphql } from "@/graphql";
import { STATE_DATA, type StateCode } from "@/lib/utils/states";
import { getRequiredFieldMessage } from "@/lib/utils/validation";
import {
  createOptionalEmailSchema,
  createOptionalIntegerSchema,
  createOptionalPhoneSchema,
  createOptionalStringSchema,
  createRequiredStringSchema,
} from "@/lib/utils/validation/zod";

export type SupplyChainPartnerRole = ReturnType<typeof graphql.scalar<"SupplyChainsSupplyChainPartnerRoleChoices">>;

export const SUPPLY_CHAIN_PARTNER_ROLES: SupplyChainPartnerRole[] = [
  "SUPPLIER",
  "CUSTOM_FEEDER",
  "OWNER_FEEDER",
  "PACKER",
  "AUCTION",
  "OTHER",
  "TRUCKING",
] as const;

export const contactSchema = z.object({
  businessName: createRequiredStringSchema("business name", 50),
  type: createRequiredStringSchema("type", 50),
  role: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.enum(SUPPLY_CHAIN_PARTNER_ROLES as [SupplyChainPartnerRole, ...SupplyChainPartnerRole[]], {
      error: getRequiredFieldMessage("role"),
    }),
  ),
  contactFirstName: createOptionalStringSchema("first name", 50),
  contactLastName: createOptionalStringSchema("last name", 50),
  contactPhone: createOptionalPhoneSchema(),
  contactEmail: createOptionalEmailSchema("contact email", 50),
  businessAccountManagerId: createOptionalStringSchema("business account manager", 50),
  addressLine1: createRequiredStringSchema("address line 1", 50),
  addressLine2: createOptionalStringSchema("address line 2", 50),
  postCode: createRequiredStringSchema("zip code", 15),
  city: createRequiredStringSchema("city", 30),
  state: z
    .enum(Object.keys(STATE_DATA) as [StateCode, ...StateCode[]])
    .optional()
    .superRefine((val, ctx) => {
      if (val === undefined) {
        ctx.addIssue({
          code: "custom",
          message: getRequiredFieldMessage("state"),
        });
      }
    }),
  countryId: createRequiredStringSchema("country", 10),
  accountNumber: createOptionalStringSchema("account number", 30),
  monthlyTarget: createOptionalIntegerSchema({
    displayName: "Monthly Target",
    min: 1,
    max: 1000000,
  }),
  note: createOptionalStringSchema("note", 500).optional(),
});

export const loqateFindAddressSchema = z.object({
  Items: z.array(
    z.object({
      Id: z.string(),
      Type: z.string(),
      Text: z.string(),
      Highlight: z.string(),
      Description: z.string(),
    }),
  ),
});

export const loqateRetrieveAddressSchema = z.object({
  Items: z.array(
    z.object({
      Line1: z.string(),
      Line2: z.string().optional(),
      City: z.string(),
      PostalCode: z.string(),
      ProvinceCode: z.string(),
    }),
  ),
});

export type LoqateFindAddress = z.infer<typeof loqateFindAddressSchema>;
